import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import StatCard from '../../lib/components/StatCard';
import {
  accessSettings,
  generateMockTimetable,
  mockPreviewUsers,
  portalAccessStats,
  portalRoles,
  type PortalRole,
  type PreviewUser,
  type TimetableSlot,
} from './MockData';

const PortalPreview: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRole, setSelectedRole] = useState<PortalRole | null>(null);
  const [selectedUser, setSelectedUser] = useState<PreviewUser | null>(null);
  const [previewTimetable, setPreviewTimetable] = useState<TimetableSlot[]>([]);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settings, setSettings] = useState(accessSettings);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const getRoleIcon = (icon: string) => {
    switch (icon) {
      case 'teacher':
        return <PersonIcon />;
      case 'student':
        return <SchoolIcon />;
      case 'parent':
        return <FamilyRestroomIcon />;
      case 'admin':
        return <AdminPanelSettingsIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const handleRoleSelect = (role: PortalRole) => {
    setSelectedRole(role);
    setSelectedUser(null);
    setPreviewTimetable([]);
  };

  const handleUserSelect = (user: PreviewUser) => {
    setSelectedUser(user);
    if (selectedRole) {
      setPreviewTimetable(generateMockTimetable(selectedRole.id, user.id));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const periods = [1, 2, 3, 4, 5];

  const getSlot = (day: string, period: number): TimetableSlot | undefined => {
    return previewTimetable.find((s) => s.day === day && s.period === period);
  };

  const availableUsers = selectedRole ? mockPreviewUsers[selectedRole.id] || [] : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Portal Preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preview how timetables appear to different user roles in the portal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setSettingsDialogOpen(true)}>
            Access Settings
          </Button>
          <Button variant="contained" startIcon={<PublishIcon />} onClick={() => setPublishDialogOpen(true)}>
            Publish to Portal
          </Button>
          <IconButton>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'Active Users',
              value: portalAccessStats.totalActiveUsers,
              icon: <PersonIcon />,
              color: theme.palette.primary.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'Teachers',
              value: portalAccessStats.teacherAccess,
              icon: <PersonIcon />,
              color: theme.palette.info.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'Students',
              value: portalAccessStats.studentAccess,
              icon: <SchoolIcon />,
              color: theme.palette.success.main,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            stat={{
              label: 'Parents',
              value: portalAccessStats.parentAccess,
              icon: <FamilyRestroomIcon />,
              color: theme.palette.warning.main,
            }}
          />
        </Grid>
      </Grid>

      {/* Publication Status */}
      <Alert
        severity="success"
        sx={{ mb: 3 }}
        action={
          <Button color="inherit" size="small" startIcon={<OpenInNewIcon />}>
            View Portal
          </Button>
        }
      >
        <Typography variant="body2">
          <strong>Published Version:</strong> {portalAccessStats.publishedVersion} •{' '}
          <strong>Last Published:</strong> {formatDate(portalAccessStats.lastPublished)}
        </Typography>
      </Alert>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Role Preview" />
          <Tab label="Access Summary" />
        </Tabs>
      </Paper>

      {/* Role Preview Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Role Selection */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Select Role
                </Typography>
                <List>
                  {portalRoles.map((role) => (
                    <ListItemButton
                      key={role.id}
                      selected={selectedRole?.id === role.id}
                      onClick={() => handleRoleSelect(role)}
                      sx={{ borderRadius: 1, mb: 1 }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: selectedRole?.id === role.id ? 'primary.main' : 'action.selected',
                          }}
                        >
                          {getRoleIcon(role.icon)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={role.name}
                        secondary={role.description}
                        primaryTypographyProps={{ fontWeight: selectedRole?.id === role.id ? 'bold' : 'normal' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* User Selection */}
            {selectedRole && availableUsers.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Preview As
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Select User</InputLabel>
                    <Select
                      value={selectedUser?.id || ''}
                      label="Select User"
                      onChange={(e) => {
                        const user = availableUsers.find((u) => u.id === e.target.value);
                        if (user) handleUserSelect(user);
                      }}
                    >
                      {availableUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Box>
                            <Typography variant="body2">{user.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.department || user.yearGroup || user.email}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Timetable Preview */}
          <Grid size={{ xs: 12, md: 9 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6">
                      {selectedUser ? `${selectedUser.name}'s Timetable` : 'Timetable Preview'}
                    </Typography>
                    {selectedUser && (
                      <Typography variant="body2" color="text.secondary">
                        Viewing as: {selectedRole?.name} • {selectedUser.department || selectedUser.yearGroup}
                      </Typography>
                    )}
                  </Box>
                  {selectedRole && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {selectedRole.permissions.map((perm) => (
                        <Chip key={perm} size="small" label={perm.replace(/_/g, ' ')} variant="outlined" />
                      ))}
                    </Box>
                  )}
                </Box>

                {!selectedUser ? (
                  <Box
                    sx={{
                      height: 400,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <VisibilityIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        Select a role and user to preview
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        Choose from the panel on the left
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Period</TableCell>
                          {days.map((day) => (
                            <TableCell key={day} align="center" sx={{ fontWeight: 'bold' }}>
                              {day}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {periods.map((period) => (
                          <TableRow key={period}>
                            <TableCell sx={{ fontWeight: 'bold' }}>P{period}</TableCell>
                            {days.map((day) => {
                              const slot = getSlot(day, period);
                              return (
                                <TableCell
                                  key={day}
                                  align="center"
                                  sx={{
                                    bgcolor: slot?.subjectColor || '#f5f5f5',
                                    color: slot?.subject === 'Free Period' ? 'text.secondary' : '#fff',
                                    p: 1,
                                  }}
                                >
                                  {slot && (
                                    <Box>
                                      <Typography variant="body2" fontWeight="bold">
                                        {slot.subject}
                                      </Typography>
                                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                        {slot.room}
                                      </Typography>
                                      {selectedRole?.id !== 'teacher' && slot.teacher !== 'You' && (
                                        <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                                          {slot.teacher}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Access Summary Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {portalRoles.map((role) => (
            <Grid key={role.id} size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>{getRoleIcon(role.icon)}</Avatar>
                    <Box>
                      <Typography variant="h6">{role.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Permissions
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                    {role.permissions.map((perm) => (
                      <Chip key={perm} size="small" label={perm.replace(/_/g, ' ')} color="primary" variant="outlined" />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    View Mode
                  </Typography>
                  <Chip
                    label={role.viewMode === 'personal' ? 'Personal Only' : role.viewMode === 'class' ? 'Class View' : 'Full Access'}
                    color={role.viewMode === 'full' ? 'success' : 'default'}
                  />

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" color="text.secondary">
                    <strong>Sample Users:</strong> {(mockPreviewUsers[role.id] || []).length} available for preview
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Portal Access Settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure who can access timetables in the portal
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.teacherAccessEnabled}
                  onChange={(e) => setSettings((prev) => ({ ...prev, teacherAccessEnabled: e.target.checked }))}
                />
              }
              label="Enable teacher access"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.studentAccessEnabled}
                  onChange={(e) => setSettings((prev) => ({ ...prev, studentAccessEnabled: e.target.checked }))}
                />
              }
              label="Enable student access"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.parentAccessEnabled}
                  onChange={(e) => setSettings((prev) => ({ ...prev, parentAccessEnabled: e.target.checked }))}
                />
              }
              label="Enable parent access"
            />

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.requireAuth}
                  onChange={(e) => setSettings((prev) => ({ ...prev, requireAuth: e.target.checked }))}
                />
              }
              label="Require authentication"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showNotes}
                  onChange={(e) => setSettings((prev) => ({ ...prev, showNotes: e.target.checked }))}
                />
              }
              label="Show lesson notes"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setSettingsDialogOpen(false)}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)}>
        <DialogTitle>Publish to Portal</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will update the live timetable visible to all users in the portal.
          </Alert>
          <Typography variant="body2">
            The current timetable version will be published and accessible to:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary={`${portalAccessStats.teacherAccess} Teachers`} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`${portalAccessStats.studentAccess} Students`} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`${portalAccessStats.parentAccess} Parents/Guardians`} />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setPublishDialogOpen(false)}>
            Publish Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalPreview;

